// ============================================================================
// ISSUE INVOICES (PMS Phase 2 slice 7a)
// ============================================================================
// Opened after the last room checks out, and from PMS → Invoices. Previews the
// stay's invoices — one per payer, the posted lines added up, never recomputed —
// with what stops them (still in house, numbering not ready), then issues them.
// The statement prints at any time. Issued invoices print from here too.
// A late charge after checkout (slice 7c) is posted on the folio (the Folio
// button) and issued here as a supplementary invoice; a disbursement shows apart,
// as a reimbursement outside the taxable value.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { invoiceService } from 'api/services/invoiceService';
import { formatCurrency } from 'utils/currencyFormat';
import { blobErrorMessage, reserveTab, showPdf } from 'utils/reportFile';
import { STORAGE_KEYS } from 'auth/storageKeys';
import ChangeSettlementModal from 'components/modals/modules/booking-module/ChangeSettlementModal';
import { bookingService } from 'api/services/bookingService';
import { settlementModeLabel } from 'api/types/settlement.types';
import type { Booking } from 'api/types/booking.types';
import {
  INVOICE_ACTIONS,
  INVOICE_MODULE,
  PAYER_LABEL,
  type Invoice,
  type InvoicePreview,
} from 'api/types/invoice.types';

interface Props {
  isOpen: boolean;
  bookingId: string | null;
  onClose: () => void;
  /** After invoices were issued. */
  onIssued?: () => void;
  /** Opens the stay's folio — for a late charge or a receipt (the caller owns the folio modal). */
  onOpenFolio?: (folioId: string) => void;
}

/** The signed-in user's actions on pms-inhouse (the folio desk). */
export const inHouseActions = (): string[] => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS) || '{}');
    return Array.isArray(all?.['pms-inhouse']) ? all['pms-inhouse'] : [];
  } catch {
    return [];
  }
};

/** The signed-in user's actions on pms-invoices, from the permissions cached at login. */
export const invoiceActions = (): string[] => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS) || '{}');
    return Array.isArray(all?.[INVOICE_MODULE]) ? all[INVOICE_MODULE] : [];
  } catch {
    return [];
  }
};

/** Opens a PDF from the API in a tab reserved inside the click. */
export const openPdf = async (fetch: () => Promise<Blob>, failed: string) => {
  const tab = reserveTab();
  try {
    showPdf(await fetch(), tab);
  } catch (err) {
    tab?.close();
    notificationService.toast.error(await blobErrorMessage(err, failed));
  }
};

const day = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
const dates = (from?: string | null, to?: string | null) =>
  !from ? '' : !to || to === from ? day(from) : `${day(from)} – ${day(to)}`;

const IssueInvoicesModal: React.FC<Props> = ({ isOpen, bookingId, onClose, onIssued, onOpenFolio }) => {
  const actions = invoiceActions();
  const canIssue = actions.includes(INVOICE_ACTIONS.issue);
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      setPreview(await invoiceService.preview(bookingId));
    } catch (err: any) {
      setPreview(null);
      setError(err?.response?.data?.message || err?.message || 'The invoice preview did not load.');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);
  useEffect(() => { if (isOpen) void load(); }, [isOpen, load]);

  // Change Settlement from here (booking-list: change-settlement), for lines billed to
  // a payer the stay's settlement no longer names (2026-10-16).
  const canChangeSettlement = (() => {
    try {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS) || '{}');
      return Array.isArray(all?.['booking-list']) && all['booking-list'].includes('change-settlement');
    } catch { return false; }
  })();
  const [settlementBooking, setSettlementBooking] = useState<Booking | null>(null);
  const openSettlement = useCallback(async () => {
    if (!bookingId) return;
    try {
      const res: any = await bookingService.getById(bookingId);
      setSettlementBooking((res?.data ?? res) as Booking);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The booking did not load.');
    }
  }, [bookingId]);

  const [postingNights, setPostingNights] = useState(false);
  const postNights = useCallback(async () => {
    if (!bookingId) return;
    setPostingNights(true);
    try {
      setPreview(await invoiceService.postRemainingNights(bookingId));
      notificationService.toast.success('Remaining nights posted.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The remaining nights were not posted.');
    } finally {
      setPostingNights(false);
    }
  }, [bookingId]);

  const issue = useCallback(async () => {
    if (!bookingId) return;
    setIssuing(true);
    try {
      const result = await invoiceService.issue(bookingId);
      notificationService.toast.success(
        `${result.invoices.length} invoice(s) issued`
        + (result.folioClosed ? ' — folio closed.' : result.guestBalance > 0 ? ` — guest owes ${formatCurrency(result.guestBalance)}.` : '.'));
      onIssued?.();
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The invoices were not issued.');
    } finally {
      setIssuing(false);
    }
  }, [bookingId, load, onIssued]);

  if (!isOpen || !bookingId) return null;

  const renderInvoice = (inv: Invoice, i: number) => (
    <div key={`${inv.billToType}-${i}`} style={{ border: '1px solid var(--border-color)', borderRadius: 8, marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline', padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderRadius: '8px 8px 0 0' }}>
        <span className="badge badge-secondary" style={{ fontSize: '0.65rem' }}>{PAYER_LABEL[inv.billToType] ?? inv.billToType}</span>
        <b>{inv.billToName}</b>
        {inv.billToGstin && <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>GSTIN {inv.billToGstin}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          {inv.documentTitle === 'TAX_INVOICE' ? 'Tax invoice' : 'Invoice (tax off)'}
        </span>
      </div>
      <table className="data-table" style={{ width: '100%', fontSize: '0.8rem' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Description</th><th style={{ textAlign: 'left' }}>Dates</th>
            <th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Rate</th>
            <th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>Tax</th><th style={{ textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {inv.lines.filter((l) => !l.isReimbursement).map((l) => (
            <tr key={l.lineNo}>
              <td>{l.description}{l.isTaxInclusive && <span className="badge badge-info" style={{ fontSize: '0.58rem', marginLeft: 4 }}>incl. tax</span>}</td>
              <td>{dates(l.dateFrom, l.dateTo)}</td>
              <td style={{ textAlign: 'right' }}>{l.quantity}</td>
              <td style={{ textAlign: 'right' }} title={l.unitRate ? undefined : 'Varies by a paisa across rooms / nights — see the statement'}>{l.unitRate ? formatCurrency(l.unitRate) : ''}</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(l.taxableAmount)}</td>
              <td style={{ textAlign: 'right' }} title={l.taxes.map((t) => `${t.componentName || t.componentCode} ${t.ratePercentage}%: ${formatCurrency(t.taxAmount)}`).join(' · ')}>
                {formatCurrency(l.taxAmount)}
              </td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(l.totalAmount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {inv.lines.some((l) => l.isReimbursement) && (
        <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>Reimbursements — paid on the guest's behalf, at cost, outside the taxable value</div>
          {inv.lines.filter((l) => l.isReimbursement).map((l) => (
            <div key={l.lineNo} style={{ display: 'flex', gap: '0.75rem' }}>
              <span>{l.description}</span><span style={{ color: 'var(--text-muted)' }}>{dates(l.dateFrom, l.dateTo)}</span>
              <span style={{ marginLeft: 'auto' }}>{formatCurrency(l.totalAmount)}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.25rem', padding: '0.4rem 0.75rem', fontSize: '0.82rem' }}>
        <span>Taxable {formatCurrency(inv.taxableAmount)}</span>
        <span>Tax {formatCurrency(inv.taxAmount)}</span>
        {!!inv.reimbursementAmount && <span>Reimbursements {formatCurrency(inv.reimbursementAmount)}</span>}
        {inv.roundOff !== 0 && <span>Round off {formatCurrency(inv.roundOff)}</span>}
        <b>Total {formatCurrency(inv.totalAmount)}</b>
      </div>
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10060 }} />
      <div className="modal-container modal-lg" style={{ zIndex: 10061 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Invoices — {preview?.bookingNumber ?? ''}{preview?.guestName ? ` · ${preview.guestName}` : ''}
          </h2>
          <button className="modal-close" onClick={onClose} disabled={issuing}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}
          {error && <div className="form-error-message">{error}</div>}
          {preview && (
            <>
              {preview.issued.length > 0 && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Issued</div>
                  {preview.issued.map((i) => (
                    <div key={i.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', fontSize: '0.85rem', padding: '0.2rem 0' }}>
                      <span style={{ fontFamily: 'monospace' }}>{i.invoiceNumber}</span>
                      <span>{i.billToName}</span>
                      <b style={{ marginLeft: 'auto' }}>{formatCurrency(i.totalAmount)}</b>
                      <button className="btn btn-secondary btn-sm" onClick={() => { void openPdf(() => invoiceService.pdf(i.id), 'The invoice did not print.'); }}>
                        <IconSystem.print size={13} /> Print
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {(!!preview.misbilledToParty || !!preview.misbilledToGuest) && (
                <div style={{ padding: '0.6rem 0.8rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem',
                  background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}>
                  <div>
                    Settlement now: <b>{settlementModeLabel(preview.settlementMode)}</b>
                    {preview.settlementFollowsParty && preview.settlementPartyName && <> (as {preview.settlementPartyName}'s master)</>}.
                    {' '}Lines posted under an earlier settlement:
                  </div>
                  {!!preview.misbilledToParty && <div>· {preview.misbilledToParty} line(s), {formatCurrency(preview.misbilledToPartyAmount ?? 0)} — billed to the guest, the settlement names {preview.settlementPartyName || 'the party'}</div>}
                  {!!preview.misbilledToGuest && <div>· {preview.misbilledToGuest} line(s), {formatCurrency(preview.misbilledToGuestAmount ?? 0)} — billed to the party, the settlement names the guest</div>}
                  {canChangeSettlement
                    ? <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.4rem' }} onClick={() => { void openSettlement(); }}>Change settlement…</button>
                    : <div style={{ marginTop: '0.3rem' }}>Ask a user with Change Settlement to re-bill them, or issue as they are.</div>}
                </div>
              )}

              {preview.blockers.length > 0 && (
                <div style={{ padding: '0.6rem 0.8rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem',
                  background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)' }}>
                  {preview.blockers.map((b) => <div key={b}>{b}</div>)}
                  {!!preview.unpostedNightLines && canIssue && (
                    <button className="btn btn-primary btn-sm" style={{ marginTop: '0.4rem' }} disabled={postingNights}
                      onClick={() => { void postNights(); }}>
                      {postingNights ? 'Posting…' : `Post remaining nights (${formatCurrency(preview.unpostedNightAmount ?? 0)})`}
                    </button>
                  )}
                </div>
              )}

              {preview.invoices.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
                    To issue on {new Date(preview.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  {preview.invoices.map(renderInvoice)}
                </>
              )}

              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Guest balance <b style={{ color: preview.guestBalance > 0 ? 'var(--danger, #dc2626)' : undefined }}>{formatCurrency(preview.guestBalance)}</b>
                {preview.guestBalance > 0 ? ' — take the payment on the folio; it closes when settled.' : preview.guestBalance < 0 ? ' (credit held)' : ''}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {onOpenFolio && preview?.folioId && inHouseActions().includes('view') && (
            <button className="btn btn-secondary" style={{ marginRight: 'auto' }} title="Post a late charge or take a payment on the stay's folio"
              onClick={() => onOpenFolio(preview.folioId!)}>
              <IconSystem.file size={14} /> Folio
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => { void openPdf(() => invoiceService.statement(bookingId), 'The statement did not print.'); }}>
            <IconSystem.file size={14} /> Statement
          </button>
          <button className="btn btn-secondary" title="Every folio line, night by night"
            onClick={() => { void openPdf(() => invoiceService.statement(bookingId, true), 'The statement did not print.'); }}>
            Detailed
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={issuing}>Close</button>
          {canIssue && preview && preview.invoices.length > 0 && (
            <button className="btn btn-primary" onClick={() => { void issue(); }} disabled={issuing || !preview.canIssue}
              title={preview.canIssue ? undefined : preview.blockers.join(' ')}>
              {issuing ? 'Issuing…' : `Issue ${preview.invoices.length} invoice${preview.invoices.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
      <ChangeSettlementModal isOpen={!!settlementBooking} booking={settlementBooking}
        onClose={() => setSettlementBooking(null)} onChanged={() => { setSettlementBooking(null); void load(); }} />
    </>
  );
};

export default IssueInvoicesModal;
